import React from 'react';
import { useIdioma } from '../../i18n/idioma';
import { X, FileText, Loader2 } from 'lucide-react';

export interface PendingAttachment {
  /** Local id used as React key and for removing before upload completes. */
  localId: string;
  file: File;
  /** Object URL for image preview; ignored for PDFs. */
  previewUrl: string;
  /** 'uploading' until storage URL is resolved, then 'ready'; 'error' on failure. */
  status: 'uploading' | 'ready' | 'error';
  /** Populated once the upload resolves. */
  remoteUrl?: string;
  errorMessage?: string;
}

interface AttachmentThumbProps {
  /** Either a pending upload or a committed attachment already in the message history. */
  attachment:
    | { kind: 'pending'; data: PendingAttachment }
    | { kind: 'committed'; url: string; mimeType: string; name: string };
  onRemove?: () => void;
  size?: 'sm' | 'md';
}

const isImageMime = (mime: string) => mime.startsWith('image/');

const AttachmentThumb: React.FC<AttachmentThumbProps> = ({ attachment, onRemove, size = 'md' }) => {
    const { t } = useIdioma();
  const dim = size === 'sm' ? 'w-12 h-12' : 'w-16 h-16';

  let imgSrc: string | undefined;
  let mimeType: string;
  let name: string;
  let statusOverlay: React.ReactNode = null;
  let errorTitle = '';

  if (attachment.kind === 'pending') {
    const { data } = attachment;
    mimeType = data.file.type;
    name = data.file.name;
    imgSrc = isImageMime(mimeType) ? data.previewUrl : undefined;
    if (data.status === 'uploading') {
      statusOverlay = (
        <div className="absolute inset-0 bg-navy-950/50 flex items-center justify-center rounded-lg">
          <Loader2 size={size === 'sm' ? 14 : 18} className="text-white animate-spin" />
        </div>
      );
    } else if (data.status === 'error') {
      errorTitle = data.errorMessage || t('adjuntos.miniatura.errorsubida');
      statusOverlay = (
        <div className="absolute inset-0 bg-peligro/80 flex items-center justify-center rounded-lg">
          <span className="label-ui !text-white">{t('adjuntos.miniatura.fallo')}</span>
        </div>
      );
    }
  } else {
    mimeType = attachment.mimeType;
    name = attachment.name;
    imgSrc = isImageMime(mimeType) ? attachment.url : undefined;
  }

  const isPdf = mimeType === 'application/pdf';

  return (
    <div className={`relative ${dim} rounded-lg border border-acero-200 bg-acero-50 overflow-hidden group shrink-0`} title={errorTitle || name}>
      {imgSrc ? (
        <img src={imgSrc} alt={name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-0.5 px-1 text-center">
          <FileText size={size === 'sm' ? 14 : 18} className="text-acero-500" />
          <span className="label-ui">{isPdf ? 'PDF' : t('adjuntos.miniatura.archivo')}</span>
        </div>
      )}
      {statusOverlay}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-navy-850 text-white flex items-center justify-center opacity-80 hover:opacity-100 shadow-s1 transition-opacity duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          aria-label={t('comun.quitarAria', { que: name })}
        >
          <X size={10} />
        </button>
      )}
    </div>
  );
};

export default AttachmentThumb;
